import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Newspaper, FileText, Video, Image as ImageIcon, ExternalLink, Calendar } from 'lucide-react';
import { toEmbedUrl, toThumbnailUrl, isDriveUrl } from '@/utils/drive';

const typeMeta = {
  pdf: { icon: FileText, label: 'PDF', color: 'bg-red-50 text-red-700' },
  video: { icon: Video, label: 'Video', color: 'bg-blue-50 text-blue-700' },
  image: { icon: ImageIcon, label: 'Imagen', color: 'bg-amber-50 text-amber-700' },
  link: { icon: ExternalLink, label: 'Enlace', color: 'bg-gray-100 text-gray-700' },
};

export default function Intranet() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getIntranet()
      .then((r) => setPosts(r.data))
      .catch(() => toast.error('Error al cargar anuncios'))
      .finally(() => setLoading(false));
  }, []);

  const fmt = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' }); }
    catch { return iso; }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="intranet-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-tight flex items-center gap-3">
            <Newspaper className="h-7 w-7 text-[#DC143C]" />
            Intranet
          </h1>
          <p className="text-gray-600 mt-1">Comunicados, políticas y avisos importantes</p>
        </div>

        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500">
            Cargando...
          </div>
        ) : posts.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500">
            <Newspaper className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No hay anuncios publicados todavía.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {posts.map((p) => {
              const meta = typeMeta[p.content_type] || typeMeta.link;
              const Icon = meta.icon;
              const embed = toEmbedUrl(p.content_url);
              return (
                <article key={p.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-testid="intranet-post">
                  <header className="px-5 sm:px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{p.title}</h2>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                        <Calendar className="h-3 w-3" />
                        {fmt(p.created_at)}
                      </p>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium uppercase ${meta.color} self-start sm:self-auto`}>
                      <Icon className="h-3 w-3" />
                      {meta.label}
                    </span>
                  </header>

                  {p.description && (
                    <div className="px-5 sm:px-6 py-4 text-sm text-gray-700 whitespace-pre-wrap">{p.description}</div>
                  )}

                  {p.content_url && (
                    <div className="border-t border-gray-100 bg-gray-50">
                      {p.content_type === 'image' ? (
                        <img
                          src={isDriveUrl(p.content_url) ? toThumbnailUrl(p.content_url, 1200) : p.content_url}
                          alt={p.title}
                          className="w-full max-h-[60vh] object-contain bg-white"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : p.content_type === 'video' ? (
                        isDriveUrl(p.content_url) ? (
                          <iframe src={embed} title={p.title} className="w-full h-[50vh] sm:h-[60vh] border-0" allow="autoplay; encrypted-media; fullscreen" allowFullScreen />
                        ) : (
                          <video src={p.content_url} controls playsInline className="w-full max-h-[60vh] bg-black" />
                        )
                      ) : p.content_type === 'pdf' ? (
                        <iframe src={embed} title={p.title} className="w-full h-[60vh] border-0 bg-white" />
                      ) : (
                        <div className="px-5 sm:px-6 py-3">
                          <a href={p.content_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#DC143C] font-medium hover:underline" data-testid="intranet-post-link">
                            <ExternalLink className="h-4 w-4" />
                            Abrir enlace
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
